import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Breadcrumb } from "@/components/AppShell";
import { CustomerSheet } from "@/components/sales/CustomerSheet";
import { DocumentPreview } from "@/components/sales/DocumentPreview";
import { HouseholdCategoryField } from "@/components/husholdning/HouseholdCategoryField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UNITS, VAT_RATES } from "@/lib/salesConstants";
import { formatKr, formatDate, todayIso, formatOrg } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, ChevronsUpDown, Check, Loader2, Eye, Save, Send, Package, UserPlus, Building2, User, AlertTriangle, GripVertical } from "lucide-react";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function computeTotals(lines) {
  let subtotal = 0;
  const byRate = {};
  for (const ln of lines) {
    const qty = Number(ln.quantity) || 0;
    const price = Number(ln.unit_price) || 0;
    const disc = Number(ln.discount_pct) || 0;
    const ex = qty * price * (1 - disc / 100);
    subtotal += ex;
    const r = Number(ln.vat_rate) || 0;
    byRate[r] = byRate[r] || { rate: r, base: 0, vat: 0 };
    byRate[r].base += ex;
    byRate[r].vat += ex * r / 100;
  }
  const breakdown = Object.values(byRate).sort((a, b) => b.rate - a.rate).map((b) => ({ rate: b.rate, base: round2(b.base), vat: round2(b.vat) }));
  const totalVat = round2(breakdown.reduce((a, b) => a + b.vat, 0));
  return { subtotal: round2(subtotal), breakdown, totalVat, total: round2(round2(subtotal) + totalVat) };
}

const newLine = (defaults = {}) => ({ id: `tmp-${Math.random().toString(36).slice(2)}`, description: "", quantity: 1, unit: defaults.default_unit || "stk", unit_price: "", discount_pct: 0, vat_rate: defaults.default_vat_rate ?? 25, product_id: null });

const addDays = (iso, days) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + Number(days || 0));
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function SalesInvoiceEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showDiscount, setShowDiscount] = useState(false);
  const [form, setForm] = useState({ customer_id: "", issue_date: todayIso(), due_date: "", delivery_date: "", your_reference: "", our_reference: "", order_reference: "", notes: "", household_category_id: "", lines: [] });
  const [dueTouched, setDueTouched] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, c, p] = await Promise.all([api.get("/sales/meta"), api.get("/sales/customers"), api.get("/sales/products")]);
        if (cancelled) return;
        setMeta(m.data);
        setCustomers(c.data.items);
        setProducts(p.data.items);
        const s = m.data.invoice_settings || {};
        if (editing) {
          const inv = (await api.get(`/sales/invoices/${id}`)).data;
          if (inv.status !== "kladd") { navigate(`/faktura/fakturaer/${id}`, { replace: true }); return; }
          setForm({ customer_id: inv.customer_id || "", issue_date: inv.issue_date || todayIso(), due_date: inv.due_date || "", delivery_date: inv.delivery_date || "", your_reference: inv.your_reference || "", our_reference: inv.our_reference || "", order_reference: inv.order_reference || "", notes: inv.notes || "", household_category_id: inv.household_category_id || "", lines: (inv.lines || []).map((l) => ({ ...l })) });
          setShowDiscount((inv.lines || []).some((l) => l.discount_pct > 0));
          setDueTouched(true);
        } else {
          const preCustomer = params.get("customer_id") || "";
          const cust = c.data.items.find((x) => x.id === preCustomer);
          const dueDays = cust?.default_due_days ?? s.default_due_days ?? 14;
          setForm((f) => ({ ...f, customer_id: preCustomer, due_date: addDays(todayIso(), dueDays), notes: s.default_note || "", our_reference: "", lines: [newLine(s)] }));
        }
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const customer = customers.find((c) => c.id === form.customer_id);
  const totals = useMemo(() => computeTotals(form.lines), [form.lines]);
  const s = meta?.invoice_settings || {};

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, newLine(s)] }));
  const addProduct = (p) => {
    setForm((f) => {
      const empty = f.lines.findIndex((l) => !l.description && !l.unit_price);
      const line = { ...newLine(s), product_id: p.id, description: p.description ? `${p.name} – ${p.description}` : p.name, unit: p.unit, unit_price: p.unit_price, vat_rate: p.vat_rate };
      const lines = empty >= 0 ? f.lines.map((l, i) => (i === empty ? line : l)) : [...f.lines, line];
      return { ...f, lines };
    });
    setProdOpen(false);
  };

  const pickCustomer = (c) => {
    setForm((f) => ({ ...f, customer_id: c.id, due_date: dueTouched ? f.due_date : addDays(f.issue_date, c.default_due_days ?? s.default_due_days ?? 14) }));
    setCustOpen(false);
  };

  const onIssueDate = (v) => {
    setForm((f) => ({ ...f, issue_date: v, due_date: dueTouched ? f.due_date : addDays(v || todayIso(), customer?.default_due_days ?? s.default_due_days ?? 14) }));
  };

  const validate = () => {
    if (!form.lines.length) return "Legg til minst én linje";
    if (form.lines.some((l) => !String(l.description || "").trim())) return "Alle linjer må ha en beskrivelse";
    if (form.lines.some((l) => l.unit_price === "" || Number.isNaN(Number(l.unit_price)))) return "Alle linjer må ha en pris";
    if (form.due_date && form.issue_date && form.due_date < form.issue_date) return "Forfallsdato kan ikke være før fakturadato";
    return "";
  };

  const payload = () => ({
    customer_id: form.customer_id || null,
    issue_date: form.issue_date || null,
    due_date: form.due_date || null,
    delivery_date: form.delivery_date || null,
    your_reference: form.your_reference || null,
    our_reference: form.our_reference || null,
    order_reference: form.order_reference || null,
    notes: form.notes || null,
    household_category_id: form.household_category_id || "",
    lines: form.lines.map((l) => ({ id: String(l.id).startsWith("tmp-") ? null : l.id, product_id: l.product_id || null, description: l.description, quantity: Number(l.quantity) || 0, unit: l.unit || "stk", unit_price: Number(l.unit_price) || 0, discount_pct: Number(l.discount_pct) || 0, vat_rate: Number(l.vat_rate) })),
  });

  const saveDraft = async () => {
    const res = editing ? await api.put(`/sales/invoices/${id}`, payload()) : await api.post("/sales/invoices", payload());
    return res.data;
  };

  const onSave = async (andIssue = false) => {
    const v = validate();
    if (v) { toast.error(v); return; }
    if (andIssue && !form.customer_id) { toast.error("Velg en kunde før du utsteder"); return; }
    setBusy(andIssue ? "issue" : "save");
    try {
      const inv = await saveDraft();
      if (andIssue) {
        const issued = await api.post(`/sales/invoices/${inv.id}/issue`);
        toast.success(`Faktura ${issued.data.number_display} er utstedt`);
        navigate(`/faktura/fakturaer/${inv.id}`);
      } else {
        toast.success("Kladd lagret");
        navigate(`/faktura/fakturaer/${inv.id}`);
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const onPreview = async () => {
    const v = validate();
    if (v) { toast.error(v); return; }
    setBusy("preview");
    try {
      const inv = await saveDraft();
      if (!editing) window.history.replaceState(null, "", `/faktura/fakturaer/${inv.id}/rediger`);
      const html = (await api.get(`/sales/invoices/${inv.id}/html`, { responseType: "text" })).data;
      setPreview({ id: inv.id, html });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (error) return <Alert variant="destructive"><AlertTitle>Kunne ikke laste editoren</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-56" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;

  const profileOk = meta?.profile_completeness?.complete;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Faktura", to: "/faktura" }, { label: "Fakturaer", to: "/faktura/fakturaer" }, { label: editing ? "Rediger kladd" : "Ny faktura" }]} />
      <PageHeader title={editing ? "Rediger kladd" : "Ny faktura"} description="Fakturanummer tildeles automatisk når fakturaen utstedes. Fram til da er den en kladd du kan endre fritt." />

      {!profileOk ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="editor-profile-incomplete">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Firmaprofilen er ikke komplett</AlertTitle>
          <AlertDescription>Du kan lagre kladder, men må fylle inn {meta.profile_completeness.missing.join(", ").toLowerCase()} før du kan utstede. <Link to="/profil" className="underline font-medium">Gå til profil</Link></AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px] items-start">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Kunde</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Popover open={custOpen} onOpenChange={setCustOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={custOpen} className="flex-1 justify-between h-11 font-normal" data-testid="editor-customer-picker">
                      {customer ? <span className="flex items-center gap-2 truncate">{customer.type === "person" ? <User className="h-4 w-4 text-muted-foreground" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}<span className="truncate">{customer.name}</span><span className="text-xs text-muted-foreground">#{customer.customer_number}</span></span> : <span className="text-muted-foreground">Velg kunde…</span>}
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card" align="start">
                    <Command>
                      <CommandInput placeholder="Søk kunde…" data-testid="editor-customer-search" />
                      <CommandList>
                        <CommandEmpty>Ingen kunder funnet.</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem key={c.id} value={`${c.name} ${c.customer_number} ${c.email || ""}`} onSelect={() => pickCustomer(c)} data-testid={`editor-customer-option-${c.id}`}>
                              <Check className={cn("h-4 w-4 mr-2", form.customer_id === c.id ? "opacity-100" : "opacity-0")} />
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="text-xs text-muted-foreground">#{c.customer_number}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem onSelect={() => { setCustOpen(false); setSheetOpen(true); }} data-testid="editor-customer-create"><UserPlus className="h-4 w-4 mr-2" /> Ny kunde…</CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" className="h-11" onClick={() => setSheetOpen(true)} data-testid="editor-new-customer-button"><UserPlus className="h-4 w-4 mr-2" /> Ny kunde</Button>
              </div>
              {customer ? (
                <div className="rounded-lg border bg-secondary/50 p-3 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="editor-customer-summary">
                  <p className="font-medium">{customer.name}</p>
                  <p className="text-muted-foreground">{customer.org_number ? `Org.nr ${formatOrg(customer.org_number)}` : customer.type === "person" ? "Privatperson" : ""}</p>
                  <p className="text-muted-foreground">{[customer.address, [customer.postal_code, customer.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || <span className="text-warn">Adresse mangler</span>}</p>
                  <p className="text-muted-foreground">{customer.email || <span className="text-warn">E-post mangler (trengs for sending)</span>}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Datoer og referanser</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label htmlFor="e-issue">Fakturadato</Label><Input id="e-issue" type="date" value={form.issue_date} onChange={(e) => onIssueDate(e.target.value)} data-testid="editor-issue-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="e-due">Forfallsdato</Label><Input id="e-due" type="date" value={form.due_date} min={form.issue_date} onChange={(e) => { setDueTouched(true); set("due_date")(e); }} data-testid="editor-due-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="e-delivery">Leveringsdato <span className="text-muted-foreground">(valgfritt)</span></Label><Input id="e-delivery" type="date" value={form.delivery_date} onChange={set("delivery_date")} data-testid="editor-delivery-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="e-yref">Deres referanse</Label><Input id="e-yref" value={form.your_reference} onChange={set("your_reference")} placeholder="Kontaktperson hos kunde" data-testid="editor-your-ref" /></div>
              <div className="space-y-1.5"><Label htmlFor="e-oref">Vår referanse</Label><Input id="e-oref" value={form.our_reference} onChange={set("our_reference")} placeholder="Ditt navn" data-testid="editor-our-ref" /></div>
              <div className="space-y-1.5"><Label htmlFor="e-order">Ordre/prosjekt</Label><Input id="e-order" value={form.order_reference} onChange={set("order_reference")} placeholder="Bestillingsnr eller prosjekt" data-testid="editor-order-ref" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Linjer</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setShowDiscount((v) => !v)} data-testid="editor-toggle-discount">{showDiscount ? "Skjul rabatt" : "Vis rabatt"}</Button>
                <Popover open={prodOpen} onOpenChange={setProdOpen}>
                  <PopoverTrigger asChild><Button variant="outline" size="sm" data-testid="editor-add-product-button"><Package className="h-4 w-4 mr-2" /> Fra produkt</Button></PopoverTrigger>
                  <PopoverContent className="w-80 p-0 bg-card" align="end">
                    <Command>
                      <CommandInput placeholder="Søk produkt…" data-testid="editor-product-search" />
                      <CommandList>
                        <CommandEmpty>Ingen produkter. <Link to="/faktura/produkter" className="underline">Opprett et</Link>.</CommandEmpty>
                        <CommandGroup>
                          {products.map((p) => (
                            <CommandItem key={p.id} value={`${p.name} ${p.sku || ""}`} onSelect={() => addProduct(p)} data-testid={`editor-product-option-${p.id}`}>
                              <span className="flex-1 truncate">{p.name}</span>
                              <span className="text-xs text-muted-foreground tnum">{formatKr(p.unit_price)}/{p.unit}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className={cn("hidden md:grid gap-2 px-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground", showDiscount ? "grid-cols-[1fr_80px_92px_112px_80px_92px_112px_36px]" : "grid-cols-[1fr_80px_92px_112px_92px_112px_36px]")}>
                <span>Beskrivelse</span><span className="text-right">Antall</span><span>Enhet</span><span className="text-right">Pris eks. mva</span>{showDiscount ? <span className="text-right">Rabatt %</span> : null}<span>MVA</span><span className="text-right">Beløp</span><span />
              </div>
              {form.lines.map((l, i) => {
                const ex = round2((Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_pct) || 0) / 100));
                return (
                  <div key={l.id} className={cn("grid gap-2 rounded-lg border p-3 md:p-1.5 md:border-transparent md:hover:border-border transition-colors grid-cols-2", showDiscount ? "md:grid-cols-[1fr_80px_92px_112px_80px_92px_112px_36px]" : "md:grid-cols-[1fr_80px_92px_112px_92px_112px_36px]")} data-testid={`editor-line-${i}`}>
                    <div className="col-span-2 md:col-span-1 flex items-center gap-1">
                      <GripVertical className="hidden md:block h-4 w-4 text-muted-foreground/40 shrink-0" />
                      <Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Hva har du levert?" aria-label="Beskrivelse" data-testid={`editor-line-desc-${i}`} />
                    </div>
                    <div className="space-y-1"><Label className="md:hidden text-xs text-muted-foreground">Antall</Label><Input type="number" step="0.01" inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="text-right" aria-label="Antall" data-testid={`editor-line-qty-${i}`} /></div>
                    <div className="space-y-1"><Label className="md:hidden text-xs text-muted-foreground">Enhet</Label>
                      <Select value={l.unit} onValueChange={(v) => setLine(i, "unit", v)}>
                        <SelectTrigger aria-label="Enhet" data-testid={`editor-line-unit-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card">{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1"><Label className="md:hidden text-xs text-muted-foreground">Pris eks. mva</Label><Input type="number" step="0.01" inputMode="decimal" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="text-right" placeholder="0,00" aria-label="Pris" data-testid={`editor-line-price-${i}`} /></div>
                    {showDiscount ? <div className="space-y-1"><Label className="md:hidden text-xs text-muted-foreground">Rabatt %</Label><Input type="number" step="0.5" min="0" max="100" inputMode="decimal" value={l.discount_pct} onChange={(e) => setLine(i, "discount_pct", e.target.value)} className="text-right" aria-label="Rabatt" data-testid={`editor-line-discount-${i}`} /></div> : null}
                    <div className="space-y-1"><Label className="md:hidden text-xs text-muted-foreground">MVA</Label>
                      <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, "vat_rate", Number(v))}>
                        <SelectTrigger aria-label="MVA-sats" data-testid={`editor-line-vat-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card">{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-end md:justify-end h-10 text-sm font-medium tnum whitespace-nowrap px-1" data-testid={`editor-line-total-${i}`}><span className="md:hidden text-xs text-muted-foreground mr-2">Beløp</span>{formatKr(ex)}</div>
                    <div className="flex items-center justify-end"><Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => removeLine(i)} aria-label="Fjern linje" data-testid={`editor-line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button></div>
                  </div>
                );
              })}
              <Button variant="outline" onClick={addLine} className="w-full border-dashed" data-testid="editor-add-line-button"><Plus className="h-4 w-4 mr-2" /> Legg til linje</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Melding på fakturaen</CardTitle></CardHeader>
            <CardContent><Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="F.eks. Takk for oppdraget! Vises nederst på fakturaen." data-testid="editor-notes" /></CardContent>
          </Card>

          {/* Regnskap: inntektskategori (valgfritt, kontrollert – lagres sammen med kladden). Rendrer ingenting når Regnskap-modulen er av. */}
          <HouseholdCategoryField source="faktura" value={form.household_category_id || null} onChange={(v) => setForm((f) => ({ ...f, household_category_id: v || "" }))} className="rounded-xl border bg-card p-4" />
        </div>

        <div className="lg:sticky lg:top-24 space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Sammendrag</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm" data-testid="editor-summary">
              <div className="flex justify-between"><span className="text-muted-foreground">Sum eks. mva</span><span className="tnum">{formatKr(totals.subtotal)}</span></div>
              {totals.breakdown.map((b) => <div key={b.rate} className="flex justify-between"><span className="text-muted-foreground">MVA {b.rate} %</span><span className="tnum">{formatKr(b.vat)}</span></div>)}
              <div className="flex justify-between border-t pt-3 mt-3"><span className="font-medium">Å betale</span><span className="font-heading text-xl font-semibold tnum" data-testid="editor-total">{formatKr(totals.total)}</span></div>
              <div className="pt-2 text-xs text-muted-foreground space-y-1">
                <p>Fakturadato {formatDate(form.issue_date)} · forfall {formatDate(form.due_date)}</p>
                <p>Neste nummer: <strong className="text-foreground">{s.number_prefix || ""}{s.next_number}</strong>{s.kid_enabled ? " · KID genereres" : ""}</p>
              </div>
            </CardContent>
          </Card>
          <div className="grid gap-2">
            <Button size="lg" onClick={() => onSave(true)} disabled={!!busy || !profileOk} data-testid="editor-issue-button">{busy === "issue" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Lagre og utsted</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => onSave(false)} disabled={!!busy} data-testid="editor-save-draft-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Lagre kladd</Button>
              <Button variant="outline" onClick={onPreview} disabled={!!busy} data-testid="editor-preview-button">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />} Forhåndsvis</Button>
            </div>
            <Button variant="ghost" onClick={() => navigate(editing ? `/faktura/fakturaer/${id}` : "/faktura/fakturaer")} disabled={!!busy} data-testid="editor-cancel-button">Avbryt</Button>
          </div>
        </div>
      </div>

      <CustomerSheet open={sheetOpen} onOpenChange={setSheetOpen} onSaved={(c) => { setCustomers((cs) => [...cs.filter((x) => x.id !== c.id), c].sort((a, b) => a.name.localeCompare(b.name))); pickCustomer(c); }} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 bg-card flex flex-col" data-testid="editor-preview-dialog">
          <DialogHeader className="px-5 py-3 border-b"><DialogTitle className="text-base">Forhåndsvisning – slik blir dokumentet</DialogTitle></DialogHeader>
          <div className="flex-1 bg-secondary/60 overflow-auto p-4">
            {preview ? <DocumentPreview html={preview.html} title="Forhåndsvisning" testId="editor-preview" /> : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
